import {
  IconButton,
  Search,
  Spacer,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextBody,
} from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import {
  CSSProperties,
  FC,
  Fragment,
  memo,
  ReactNode,
  useMemo,
  useState,
} from 'react';
import './permissions-table.scss';
import {
  filterOrgsByName,
  resolveRolesTableSearch,
  resolveRolesTableSearchPlaceholder,
} from './permissions-table-filter';
import { TableRowContent } from './table-row-content';
import {
  HandleAddProject,
  HandleAddTeam,
  HandleAllOrgsCollapse,
  HandleOrgCollapse,
  HandleRemoveProjectWithRoles,
  HandleRemoveTeam,
  HandleToggleFavoriteOrg,
  HandleUpdateManagerRole,
  HandleUpdateProjectProductRole,
  ShortOrgType,
} from './permissions-table.types';

// The usual "visible to assistive technology only" box.
const VISUALLY_HIDDEN: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  margin: '-1px',
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export type PermissionsTableProps = {
  orgs: ShortOrgType[];
  canManageAccess: boolean;
  isAddingTeamToOrg?: boolean;
  isSavingDetails: boolean;
  isPypestreamDataTeamEligible: boolean;
  favoriteOrgIds: string[];
  canEditFavorites: boolean;
  handleAllOrgsCollapse: HandleAllOrgsCollapse;
  handleOrgCollapse: HandleOrgCollapse;
  handleUpdateManagerRole: HandleUpdateManagerRole;
  handleRemoveTeam?: HandleRemoveTeam;
  handleAddTeam?: HandleAddTeam;
  handleUpdateProjectProductRole: HandleUpdateProjectProductRole;
  handleAddProject: HandleAddProject;
  handleRemoveProjectWithRoles: HandleRemoveProjectWithRoles;
  handleToggleFavoriteOrg: HandleToggleFavoriteOrg;
  /** Left side of the row above the table, beside the search (e.g. a caption). */
  toolbarStart?: ReactNode;
  /** Rendered under the search, in the same column; sizes the search to itself. */
  orgFilter?: ReactNode;
  /** Number shown in the search placeholder ("Search all N Orgs"), like the org menu's. Defaults to the rows passed in. */
  totalOrgsCount?: number;
};

export const PermissionsTable: FC<PermissionsTableProps> = ({
  orgs,
  canManageAccess,
  isAddingTeamToOrg,
  isSavingDetails,
  isPypestreamDataTeamEligible,
  favoriteOrgIds,
  canEditFavorites,
  handleAllOrgsCollapse,
  handleOrgCollapse,
  handleUpdateManagerRole,
  handleRemoveTeam,
  handleAddTeam,
  handleUpdateProjectProductRole,
  handleAddProject,
  handleRemoveProjectWithRoles,
  handleToggleFavoriteOrg,
  toolbarStart,
  orgFilter,
  totalOrgsCount,
}) => {
  const [t] = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  // No debounce: the match is a substring test over the orgs already in memory,
  // so a keystroke costs one pass over a short list. `orgs` arrives already cut
  // down by the My / All / Hidden mode on the user page, so the two filters AND.
  // The threshold is the list on screen (`orgs`, already cut by My / All /
  // Hidden): "My Orgs" holding one org has nothing to look through. The
  // placeholder, by contrast, names the account's total, like the org menu's.
  const { showSearch, appliedTerm } = resolveRolesTableSearch({
    orgCount: orgs.length,
    term: searchTerm,
  });
  const placeholder = resolveRolesTableSearchPlaceholder({
    listedCount: orgs.length,
    totalCount: totalOrgsCount ?? orgs.length,
  });
  const searchedOrgs = useMemo(
    () => filterOrgsByName(orgs, appliedTerm),
    [orgs, appliedTerm]
  );
  const hasActiveSearch = appliedTerm.trim() !== '';
  const noResults = hasActiveSearch && searchedOrgs.length === 0;

  // The header controls below read `searchedOrgs`, the rows on screen: the
  // chevron and the bold/plain header text follow what the user can see. The
  // collapse-all handlers still act on every org the page holds (they are
  // page-level events that take no org list), so a click while searching also
  // collapses or expands the orgs the search is hiding.
  return (
    <>
      {/* One row: `toolbarStart` on the left (top-aligned: `Stack` defaults to
          alignItems="start"), the right column (search, then `orgFilter`) on
          the right. Without `toolbarStart`, "space-between" would pin a lone
          child to the left, hence "end". */}
      <Stack justifyContent={toolbarStart ? 'space-between' : 'end'}>
        {toolbarStart}
        {/* The column is a flex item of the row above with no `flex` or
            `width` set, so it is as wide as its widest child. With `orgFilter`
            that is meant to be the segmented control, and the search (no
            max-width then) is stretched to it by alignItems="stretch". The
            search's own intrinsic width is not measured here: if it exceeds the
            control's, the column follows the search. */}
        <Stack direction="column" alignItems="stretch" gutter="xsmall">
          <Search
            test-id="roles-table-search"
            size="large"
            // Not removed when it is not offered: `visibility` (unlike
            // `display`) keeps the box, so the table below does not jump when
            // the org count crosses the threshold. Hidden this way it is out of
            // the tab order and the accessibility tree.
            style={{
              ...(orgFilter ? {} : { maxWidth: '240px' }),
              ...(showSearch ? {} : { visibility: 'hidden' }),
            }}
            // `ps-search` has no aria-label property: its inner <input> is named
            // by the placeholder, so the placeholder is the accessible name.
            placeholder={
              t(`manager/common:permissionAndAccessTable.${placeholder.key}`, {
                count: placeholder.count,
              }) || `Search ${placeholder.count} Orgs`
            }
            value={searchTerm}
            onInput={(e) => setSearchTerm(String(e.target?.value ?? ''))}
          />
          {orgFilter}
        </Stack>
      </Stack>
      {/* The same message as the empty row below, for a screen reader. The row
          appears together with its text, and a live region that appears with
          its text is not reliably announced (WCAG 4.1.3), so this one is
          mounted from the first render and only its text changes. Clipped, not
          `display: none`, which would take it out of the accessibility tree. */}
      <div role="status" style={VISUALLY_HIDDEN}>
        {noResults
          ? t('manager/common:permissionAndAccessTable.noSearchResults') ||
            'No orgs match your search'
          : ''}
      </div>
      <Spacer size="medium" />
      <Table size="large" bordered className="c-permissions-table">
        <TableHead>
          <TableRow className="background-gray">
            <TableCell
              variant="th"
              i18nKey="manager/common:permissionAndAccessTable.orgTitle"
            >
              <div className="table-expand-collapse-button">
                {searchedOrgs.some((org) => !org.isCollapsed) ? (
                  <IconButton
                    name="chevrons-down-up"
                    size="xsmall"
                    onClick={() => {
                      handleAllOrgsCollapse({ collapse: true });
                    }}
                  />
                ) : (
                  <IconButton
                    name="chevrons-up-down"
                    size="xsmall"
                    onClick={() => {
                      handleAllOrgsCollapse({ collapse: false });
                    }}
                  />
                )}
                <strong>Org</strong>
              </div>
            </TableCell>
            <TableCell
              variant="th"
              i18nKey="manager/common:permissionAndAccessTable.projectTitle"
            >
              <strong>Project</strong>
            </TableCell>
            <TableCell
              variant="th"
              i18nKey="manager/common:permissionAndAccessTable.toolTitle"
            >
              {searchedOrgs[0]?.isCollapsed ? 'Tool' : <strong>Tool</strong>}
            </TableCell>
            <TableCell
              variant="th"
              i18nKey="manager/common:permissionAndAccessTable.roleTitle"
            >
              {searchedOrgs[0]?.isCollapsed ? 'Role' : <strong>Role</strong>}
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {searchedOrgs.map((org: ShortOrgType) => (
            <Fragment key={org.id}>
              <TableRowContent
                key={org.id}
                org={org}
                isAddingTeamToOrg={!!isAddingTeamToOrg}
                canManageAccess={org.canManageAccess ?? canManageAccess}
                isSavingDetails={isSavingDetails}
                isPypestreamDataTeamEligible={isPypestreamDataTeamEligible}
                favoriteOrgIds={favoriteOrgIds}
                canEditFavorites={canEditFavorites}
                handleOrgCollapse={handleOrgCollapse}
                handleUpdateManagerRole={handleUpdateManagerRole}
                handleRemoveTeam={handleRemoveTeam}
                handleAddTeam={handleAddTeam}
                handleUpdateProjectProductRole={handleUpdateProjectProductRole}
                handleAddProject={handleAddProject}
                handleRemoveProjectWithRoles={handleRemoveProjectWithRoles}
                handleToggleFavoriteOrg={handleToggleFavoriteOrg}
              />
            </Fragment>
          ))}
          {noResults && (
            <TableRow>
              <TableCell colspan={4}>
                <TextBody
                  variant="tertiary"
                  i18nKey="manager/common:permissionAndAccessTable.noSearchResults"
                >
                  No orgs match your search
                </TextBody>
              </TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell colspan={5} />
          </TableRow>
        </TableBody>
      </Table>
    </>
  );
};

export default memo(PermissionsTable);
